import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import './kanvan.style.css';
import { FaFire } from '@react-icons/all-files/fa/FaFire';
import { FaSync } from '@react-icons/all-files/fa/FaSync';
import { FaInbox } from '@react-icons/all-files/fa/FaInbox';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaTag } from '@react-icons/all-files/fa/FaTag';
import KanbanForm from './KanbanForm';
import ShieldIcon from '../commons/ShieldIcon';

const API_URL = '/api/usuario/me/actividad';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Column definitions ────────────────────────────────────────────────────────

interface ColumnDef {
  id: string;
  title: string;
  color: string;
  estados: string[];
  taskColor: TaskColor;
}
type TaskColor = 'yellow' | 'red' | 'white' | 'gray' | 'blue' | 'green';

const COLUMNS: ColumnDef[] = [
  { id: 'BLOQUEADO', title: 'Bloqueado', color: '#64748b', estados: ['BLOQUEADO', 'BLOCKED'], taskColor: 'gray' },
  { id: 'NO_AUTORIZADO', title: 'No Autorizado', color: 'red', estados: ['BLOQUEADO', 'BLOCKED'], taskColor: 'gray' },
  { id: 'PENDIENTE', title: 'Pendiente', color: '#f59e0b', estados: ['PENDIENTE', 'GENERADO', 'TODO', 'PENDING'], taskColor: 'yellow' },
  { id: 'PENDIENTE_DE_AUTORIZACION', title: 'Pendiente de Autorización', color: '#8b5cf6', estados: ['PENDIENTE_DE_AUTORIZACION'], taskColor: 'yellow' },
  { id: 'EN_PROCESO', title: 'En Proceso', color: '#3b82f6', estados: ['EN_PROCESO', 'EN_PROCESO', 'INPROGRESS'], taskColor: 'blue' },
  { id: 'FINALIZADO', title: 'Finalizado', color: '#10b981', estados: ['FINALIZADO', 'DONE', 'COMPLETADO', 'FINISHED'], taskColor: 'green' },
];

function resolveColumn(estado?: string): ColumnDef {
  if (!estado) return COLUMNS[1];
  const up = estado.toUpperCase();
  return COLUMNS.find(c => c.estados.includes(up)) ?? { ...COLUMNS[1], id: up, title: estado };
}

interface Task {
  id: string;
  asunto: string;
  urgente: boolean;
  color: TaskColor;
  requiereAutorizacion: boolean;
  raw: any;
}

interface Column {
  def: ColumnDef;
  tasks: Task[];
}

// ── Task Detail Panel ─────────────────────────────────────────────────────────

function TaskDetail({ task, onClose, onFormSuccess }: { task: Task; onClose: () => void; onFormSuccess: (msg: string) => void }) {
  const raw = task.raw;

  const field = (label: string, value: any, icon?: React.ReactNode) =>
    value != null && value !== '' ? (
      <div style={d.field}>
        {icon && <span style={d.fieldIcon}>{icon}</span>}
        <span style={d.fieldLabel}>{label}</span>
        <span style={d.fieldValue}>{String(value)}</span>
      </div>
    ) : null;

  return (
    <div style={d.root}>
      {/* Header */}
      <div style={d.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {task.urgente && <FaFire size={16} color="#f97316" title="Urgente" />}
          <span style={d.title}>{task.asunto}</span>
        </div>
        <button style={d.closeBtn} onClick={onClose} title="Cerrar">
          <FaTimes size={14} />
        </button>
      </div>

      <div style={d.body}>
        {/* Estado badge */}
        <div style={{ marginBottom: 12 }}>
          {task.urgente && (
            <span style={{
              marginLeft: 6, display: 'inline-block', padding: '3px 12px', borderRadius: 20,
              fontSize: 12, fontWeight: 700, background: '#fef2f2', color: '#b91c1c',
            }}>
              🔴 Urgente
            </span>
          )}
        </div>

        {/* Dynamic form for this task */}
        <KanbanForm
          taskId={raw.id}
          onCancel={onClose}
          onSuccess={(_vals, action) => {
            onClose();
            onFormSuccess(`Formulario "${action?.label ?? 'acción'}" enviado correctamente`);
          }}
        />

        <div style={d.divider} />

        {/* Fields */}
        <div style={d.fields}>
          {field('ID', raw.id)}
          {field('Asunto', raw.asunto)}
          {field('Descripción', raw.descripcion ?? raw.mensaje)}
          {field('Fecha', raw.fecha ?? raw.fechaInicio, <FaCalendarAlt size={12} />)}
          {field('Empresa RFC', raw.empresa?.rfc, <FaBuilding size={12} />)}
          {field('Empresa', raw.empresa?.razonSocial)}
          {field('Workflow', raw.workflow?.nombre ?? raw.workflowNombre)}
          {field('Actividad', raw.actividad?.nombre ?? raw.actividadNombre)}
          {field('Resultado', raw.resultado)}
          {raw.tags?.length > 0 && (
            <div style={d.field}>
              <span style={d.fieldIcon}><FaTag size={12} /></span>
              <span style={d.fieldLabel}>Etiquetas</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {raw.tags.map((t: any, i: number) => (
                  <span key={i} style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: t.color === 'yellow' ? '#fef3c7' : t.color === 'green' ? '#d1fae5' : '#fef2f2',
                    color: t.color === 'yellow' ? '#92400e' : t.color === 'green' ? '#065f46' : '#991b1b',
                  }}>{t.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Raw extra fields (any remaining) */}
        {/* <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>Ver datos completos</summary>
          <pre style={{ fontSize: 11, color: '#475569', marginTop: 6, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(raw, null, 2)}
          </pre>
        </details> */}
      </div>
    </div>
  );
}

const d: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', fontFamily: 'Google Sans,Roboto,Arial,sans-serif' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid #f1f3f4', background: '#fafafa', flexShrink: 0 },
  title: { fontSize: 15, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  closeBtn: { all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', color: '#64748b', flexShrink: 0 },
  body: { flex: 1, overflowY: 'auto', padding: '14px 16px' },
  fields: { display: 'flex', flexDirection: 'column', gap: 8 },
  field: { display: 'flex', alignItems: 'baseline', gap: 8 },
  fieldIcon: { color: '#94a3b8', flexShrink: 0 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#64748b', minWidth: 100, flexShrink: 0 },
  fieldValue: { fontSize: 13, color: '#1e293b', flex: 1 },
  divider: { height: 1, background: '#f1f3f4', margin: '12px 0' },
};

// ── Main component ────────────────────────────────────────────────────────────

const MIN_TOP_PX = 120;
const DEFAULT_SPLIT = 62; // % of container height for kanban panel

const KanbanBoard: React.FC = () => {
  const [columns, setColumns] = useState<Column[]>(COLUMNS.map(def => ({ def, tasks: [] })));
  const [loading, setLoading] = useState(false);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [splitPct, setSplitPct] = useState(DEFAULT_SPLIT);
  const [toast, setToast] = useState<{ msg: string; kind: 'error' | 'success' | 'warn' } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingBar = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string, kind: 'error' | 'success' | 'warn' = 'error', ms = 5000) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, kind });
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };

  // ── Data ───────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: any[] = await res.json();

      const colMap: Record<string, Task[]> = {};
      data.forEach((item: any) => {
        const col = resolveColumn(item.estado);
        if (!colMap[col.id]) colMap[col.id] = [];
        colMap[col.id].push({
          id: String(item.id ?? Math.random()),
          asunto: item.asunto ?? item.titulo ?? item.nombre ?? `#${item.id}`,
          urgente: !!item.urgente,
          color: item.urgente ? 'red' : col.taskColor,
          requiereAutorizacion: item.requiereAutorizacion,
          raw: item,
        });
      });

      const knownIds = new Set(COLUMNS.map(c => c.id));
      const extraIds = Object.keys(colMap).filter(id => !knownIds.has(id));
      setColumns([
        ...COLUMNS.map(def => ({ def, tasks: colMap[def.id] ?? [] })),
        ...extraIds.map(id => ({
          def: { id, title: id, color: '#94a3b8', estados: [id], taskColor: 'white' as TaskColor },
          tasks: colMap[id] ?? [],
        })),
      ]);
    } catch (e: any) {
      showToast(e?.message ?? 'Error al cargar actividades', 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // ── Resize bar drag ────────────────────────────────────────────────────────
  const onBarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingBar.current = true;

    const onMove = (mv: MouseEvent) => {
      if (!isDraggingBar.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const rawPct = ((mv.clientY - rect.top) / rect.height) * 100;
      const minPct = (MIN_TOP_PX / rect.height) * 100;
      setSplitPct(Math.max(minPct, Math.min(rawPct, 90)));
    };
    const onUp = () => { isDraggingBar.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Drag & drop tasks ──────────────────────────────────────────────────────
  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    // Block move if this card is currently open in the detail panel
    if (activeTask && draggableId === activeTask.id) return;
    // No-op if dropped in the same column at same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const srcIdx = columns.findIndex(c => c.def.id === source.droppableId);
    const destIdx = columns.findIndex(c => c.def.id === destination.droppableId);
    if (srcIdx === -1 || destIdx === -1) return;

    // Snapshot for rollback
    const previousColumns = columns.map(c => ({ ...c, tasks: [...c.tasks] }));

    // Optimistic update
    const newCols = columns.map(c => ({ ...c, tasks: [...c.tasks] }));
    const [moved] = newCols[srcIdx].tasks.splice(source.index, 1);
    newCols[destIdx].tasks.splice(destination.index, 0, moved);
    setColumns(newCols);

    // Play slide sound
    try {
      const audio = new Audio('/assets/audio/sliding-card.wav');
      audio.volume = 0.6;
      audio.play().catch(() => { });
    } catch { }

    // Persist to backend only when the column actually changed
    if (source.droppableId !== destination.droppableId) {
      const newEstado = destination.droppableId;
      fetch(`/api/usuario/me/actividad/${draggableId}/mover`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ estado: newEstado }),
      }).then(async res => {
        if (!res.ok) {
          setColumns(previousColumns);
          let msg = `No se pudo mover la tarea a "${newEstado}"`;
          try {
            const text = await res.text();
            if (text) {
              try {
                const body = JSON.parse(text);
                msg = body?.details ?? body?.detail ?? body?.message ?? body?.title ?? body?.error ?? text;
              } catch { msg = text; }
            }
          } catch { /* ignore */ }
          showToast(msg, 'error');
        } else {
          showToast(`Tarea movida a "${newEstado}" correctamente`, 'success');
          loadData();
        }
      }).catch(() => {
        setColumns(previousColumns);
        showToast('Error de red al mover la tarea.', 'error');
      });
    }
  };

  const detailOpen = activeTask !== null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} style={{ ...s.root, flexDirection: 'column' }}>

      {/* ── TOP: Kanban board ── */}
      <div style={{ ...s.topPanel, height: detailOpen ? `${splitPct}%` : '100%' }}>

        {/* Toolbar */}
        <div style={s.toolbar}>
          <span style={s.toolbarTitle}>Mis Actividades</span>
          <button style={s.refreshBtn} onClick={loadData} disabled={loading} title="Actualizar">
            <FaSync style={{
              fontSize: 14, color: loading ? '#6366f1' : '#5f6368',
              animation: loading ? 'spin 0.7s linear infinite' : undefined
            }} />
          </button>
        </div>

        {toast && (
          <div style={{ ...s.toast, ...s[`toast_${toast.kind}`] }}>
            {toast.kind === 'error' && '✖ '}
            {toast.kind === 'success' && '✔ '}
            {toast.kind === 'warn' && '⚠ '}
            {toast.msg}
            <button style={s.toastClose} onClick={() => setToast(null)}>✕</button>
          </div>
        )}

        {(() => {
          const isEmpty = !loading && columns.every(c => c.tasks.length === 0);
          return isEmpty ? (
            <div style={s.empty}>
              <FaInbox style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 12 }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>No hay Tareas</span>
              <span style={{ fontSize: 13, color: '#cbd5e1', marginTop: 4 }}>No tienes actividades asignadas en este momento.</span>
            </div>
          ) : null;
        })()}

        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{ ...s.board, display: !loading && columns.every(c => c.tasks.length === 0) ? 'none' : 'flex' }}>
            {columns.map(col => (
              <Droppable droppableId={col.def.id} key={col.def.id}>
                {(provided, snapshot) => (
                  <div
                    style={{ ...s.column, background: snapshot.isDraggingOver ? '#f0f9ff' : '#f8fafc', borderTop: `3px solid ${col.def.color}` }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <div style={s.colHeader}>
                      <span style={{ ...s.colTitle, color: col.def.color }}>{col.def.title}</span>
                      <span style={{ ...s.colCount, background: col.def.color }}>{col.tasks.length}</span>
                    </div>

                    <div style={s.taskList}>
                      {col.tasks.map((task, index) => (
                        <Draggable draggableId={task.id} index={index} key={task.id}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...(activeTask?.id === task.id ? {} : provided.dragHandleProps)}
                              className={`postit postit-${task.color}`}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: activeTask?.id === task.id ? 0.6 : snapshot.isDragging ? 0.85 : 1,
                                boxShadow: snapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.18)' : '0 2px 6px rgba(0,0,0,0.08)',
                                outline: activeTask?.id === task.id ? '2px solid #3b82f6' : undefined,
                                cursor: activeTask?.id === task.id ? 'not-allowed' : undefined,
                                filter: activeTask?.id === task.id ? 'grayscale(30%)' : undefined,
                              }}
                            >
                              <div style={s.taskTop}>
                                {task.urgente && <FaFire size={14} color="#f97316" title="Urgente" style={{ flexShrink: 0 }} />}
                                <span style={s.taskLabel}>{task.asunto}</span>
                                <ShieldIcon show={task.requiereAutorizacion}></ShieldIcon>
                              </div>

                              {task.raw.empresa && (
                                <span style={s.taskMeta}>
                                  {task.raw.empresa?.rfc ?? task.raw.empresa?.razonSocial ?? ''}
                                </span>
                              )}
                              {task.raw.autorizacion ? '[Autorización]' : '-'}

                              {col.def.id === 'EN_PROCESO' && (
                                <button
                                  style={s.abrirBtn}
                                  onClick={e => { e.stopPropagation(); setActiveTask(task); }}
                                >
                                  Abrir
                                </button>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {col.tasks.length === 0 && <div style={s.emptyCol}>Sin tareas</div>}
                    </div>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>
      </div>

      {/* ── RESIZE BAR ── */}
      {detailOpen && (
        <div
          style={s.resizeBar}
          onMouseDown={onBarMouseDown}
          title="Arrastrar para redimensionar"
        >
          <div style={s.resizeHandle} />
        </div>
      )}

      {/* ── BOTTOM: Task detail panel ── */}
      {detailOpen && (
        <div style={{ ...s.bottomPanel, height: `${100 - splitPct}%` }}>
          <TaskDetail
            task={activeTask!}
            onClose={() => setActiveTask(null)}
            onFormSuccess={msg => showToast(msg, 'success')}
          />
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInSlide {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default KanbanBoard;

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex', height: '100vh',
    fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
    overflow: 'hidden',
    userSelect: 'none',
  },
  topPanel: {
    display: 'flex', flexDirection: 'column', gap: 10,
    padding: '12px 16px 0',
    overflow: 'hidden',
    transition: 'height 0s',
    flexShrink: 0,
  },
  bottomPanel: {
    flexShrink: 0,
    overflow: 'hidden',
    borderTop: '1px solid #e2e8f0',
    transition: 'height 0s',
  },
  resizeBar: {
    height: 8, background: '#f1f5f9', cursor: 'row-resize',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    borderTop: '1px solid #e2e8f0',
    borderBottom: '1px solid #e2e8f0',
  },
  resizeHandle: {
    width: 40, height: 3, borderRadius: 2,
    background: '#cbd5e1',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0,
  },
  toolbarTitle: { fontSize: 18, fontWeight: 700, color: '#0f172a' },
  refreshBtn: {
    all: 'unset', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: '50%',
    border: '1px solid #e2e8f0', background: '#fff',
  },
  toast: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px 8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
    flexShrink: 0, animation: 'fadeInSlide 0.2s ease',
  },
  toast_error: { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' },
  toast_success: { background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' },
  toast_warn: { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
  toastClose: {
    all: 'unset', cursor: 'pointer', marginLeft: 'auto', fontSize: 11,
    opacity: 0.6, lineHeight: 1,
  } as React.CSSProperties,
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    flex: 1, padding: '40px 24px',
  },
  board: {
    display: 'flex', flexDirection: 'row', gap: 10,
    flex: 1, overflowX: 'auto', overflowY: 'hidden',
    paddingBottom: 8,
  },
  column: {
    flex: '0 0 230px', minWidth: 230,
    borderRadius: 12, display: 'flex', flexDirection: 'column',
    border: '1px solid #e2e8f0', overflow: 'hidden',
    transition: 'background 0.15s',
  },
  colHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0,
  },
  colTitle: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.05em' },
  colCount: {
    fontSize: 11, fontWeight: 700, color: '#fff',
    padding: '1px 7px', borderRadius: 10, minWidth: 18, textAlign: 'center' as const,
  },
  taskList: {
    display: 'flex', flexDirection: 'column', gap: 0,
    padding: '6px 2px', flex: 1, overflowY: 'auto' as const, minHeight: 40,
  },
  taskTop: { display: 'flex', alignItems: 'flex-start', gap: 6 },
  taskLabel: { fontSize: 12, fontWeight: 600, color: '#1e293b', lineHeight: 1.4, flex: 1, wordBreak: 'break-word' as const },
  taskMeta: { fontSize: 10, color: '#64748b', marginTop: 4, display: 'block' },
  emptyCol: { fontSize: 12, color: '#cbd5e1', textAlign: 'center' as const, padding: '12px 6px', fontStyle: 'italic' },
  abrirBtn: {
    marginTop: 8, padding: '4px 14px',
    background: '#3b82f6', color: '#fff',
    border: 'none', borderRadius: 6,
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    display: 'inline-block', alignSelf: 'flex-start' as const,
  },
};
